import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import Button from '../Button/Button';
import css from './StripeCardElement.module.css';

const StripeCardElement = props => {
  const {
    publishableKey,
    handleSubmitForm,
    progress,
    shouldDisable,
    payinTotal,
    intl,
    currentUser,
  } = props;

  const [stripe, setStripe] = useState(null);
  const [elements, setElements] = useState(null);
  const [card, setCard] = useState(null);
  const [cardError, setCardError] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [useSavedCard, setUseSavedCard] = useState(false);

  // Get saved Stripe card from currentUser
  const stripeCustomer = currentUser?.stripeCustomer;
  const defaultPaymentMethod = stripeCustomer?.defaultPaymentMethod;
  const savedCard = defaultPaymentMethod?.attributes?.card;
  const savedPaymentMethodId = defaultPaymentMethod?.attributes?.stripePaymentMethodId;
  const hasSavedCard = !!(savedCard && savedPaymentMethodId);

  // Auto-select saved card if available
  useEffect(() => {
    if (hasSavedCard) {
      setUseSavedCard(true);
    }
  }, [hasSavedCard]);

  useEffect(() => {
    // Only mount the card element when NOT using saved card
    if (useSavedCard) return;

    if (!window.Stripe) {
      console.error('Stripe.js has not loaded yet.');
      return;
    }
    if (!publishableKey) {
      console.error('Stripe publishableKey is missing.');
      return;
    }

    const stripeInstance = window.Stripe(publishableKey);
    const elementsInstance = stripeInstance.elements();

    const cardElement = elementsInstance.create('card', {
      style: {
        base: {
          fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", sans-serif',
          fontSize: '16px',
          color: '#141718',
          '::placeholder': {
            color: '#b2b2b2',
          },
        },
        invalid: {
          color: '#fa755a',
          iconColor: '#fa755a',
        },
      },
    });

    // Wait for the DOM element to exist
    const mountPoint = document.getElementById('stripe-card-mount-point');
    if (mountPoint) {
      cardElement.mount(mountPoint);
    }

    cardElement.on('change', event => {
      setCardComplete(event.complete);
      setCardError(event.error ? event.error.message : null);
    });

    setStripe(stripeInstance);
    setElements(elementsInstance);
    setCard(cardElement);

    return () => {
      cardElement.destroy();
    };
  }, [publishableKey, useSavedCard]);

  // Ensure stripe instance is available even when using saved card
  useEffect(() => {
    if (useSavedCard && !stripe && publishableKey && window.Stripe) {
      setStripe(window.Stripe(publishableKey));
    }
  }, [useSavedCard, stripe, publishableKey]);

  const handlePayClick = async e => {
    e.preventDefault();

    if (useSavedCard && hasSavedCard) {
      // Pay with saved card
      if (!stripe || progress || shouldDisable || isProcessingPayment) return;

      setIsProcessingPayment(true);
      try {
        await handleSubmitForm({
          stripe,
          card: null,
          savedPaymentMethodId,
        });
      } catch (err) {
        console.error('[stripe-card-element] saved card submit error:', err);
      } finally {
        setIsProcessingPayment(false);
      }
    } else {
      // Pay with new card
      if (!stripe || !elements || !card || !cardComplete || progress || shouldDisable || isProcessingPayment) {
        return;
      }

      setIsProcessingPayment(true);
      try {
        await handleSubmitForm({ stripe, card });
      } catch (err) {
        console.error('[stripe-card-element] submit error:', err);
      } finally {
        setIsProcessingPayment(false);
      }
    }
  };

  const isSubmitDisabled = useSavedCard
    ? (!hasSavedCard || progress || shouldDisable || isProcessingPayment)
    : (!cardComplete || progress || shouldDisable || isProcessingPayment);

  const brandLabel = (savedCard?.brand || 'Card').toUpperCase();
  const last4 = savedCard?.last4Digits || '••••';
  const expMonth = String(savedCard?.expirationMonth || '').padStart(2, '0');
  const expYear = String(savedCard?.expirationYear || '').slice(-2);

  return (
    <div className={css.stripeCardWrapper}>
      {/* Saved Card Section */}
      {hasSavedCard ? (
        <div className={css.savedCardSection}>
          <div
            className={classNames(css.savedCardOption, useSavedCard && css.savedCardOptionActive)}
            onClick={() => setUseSavedCard(true)}
          >
            <div className={css.savedCardRadio}>
              <div className={classNames(css.radioOuter, useSavedCard && css.radioOuterActive)}>
                {useSavedCard && <div className={css.radioInner} />}
              </div>
            </div>
            <div className={css.savedCardInfo}>
              <div className={css.savedCardBrand}>{brandLabel}</div>
              <div className={css.savedCardDetails}>
                •••• {last4} &nbsp;|&nbsp; {expMonth}/{expYear}
              </div>
            </div>
          </div>

          <div
            className={classNames(css.newCardOption, !useSavedCard && css.newCardOptionActive)}
            onClick={() => setUseSavedCard(false)}
          >
            <div className={css.savedCardRadio}>
              <div className={classNames(css.radioOuter, !useSavedCard && css.radioOuterActive)}>
                {!useSavedCard && <div className={css.radioInner} />}
              </div>
            </div>
            <div className={css.newCardLabel}>
              <FormattedMessage id="StripeCardElement.useNewCard" defaultMessage="Use a different card" />
            </div>
          </div>
        </div>
      ) : null}

      {/* New Card Input */}
      {!useSavedCard ? (
        <div className={css.stripeCardContainer}>
          <label className={css.label}>
            <FormattedMessage id="StripeCardElement.paymentCardDetails" defaultMessage="Payment card details" />
          </label>
          <div id="stripe-card-mount-point" className={css.cardInputMount} />
          {cardError ? <div className={css.error}>{cardError}</div> : null}
        </div>
      ) : null}

      <Button
        className={css.submitButton}
        type="submit"
        inProgress={progress || isProcessingPayment}
        disabled={isSubmitDisabled}
        onClick={handlePayClick}
      >
        <FormattedMessage id="CheckoutPage.confirmBooking" defaultMessage="Confirm Booking" />
      </Button>
    </div>
  );
};

export default StripeCardElement;
